import ApartmentSharpIcon from "@mui/icons-material/ApartmentSharp";
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic";
import EnergySavingsLeafIcon from "@mui/icons-material/EnergySavingsLeaf";
import PersonIcon from "@mui/icons-material/Person";
import SchemaIcon from "@mui/icons-material/Schema";
import SellIcon from "@mui/icons-material/Sell";
import { Tooltip } from "@mui/material";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import { forwardRef, useContext, useMemo } from "react";
import { ConstantContext } from "components/context/ConstantProvider";
import { useTag } from "models/tags/apiTag";

export const ColorTag = forwardRef(({ tagId, ...props }, ref) => {
  const { data: tag, isSuccess, isError } = useTag(tagId);

  const icon = useMemo(() => {
    if (!tag) return undefined;
    if (tag.type_tag_detail.code == "TAG_SECTOR")
      return <AutoAwesomeMosaicIcon />;
    if (tag.type_tag_detail.code == "TAG_IMPACT_MODEL")
      return <EnergySavingsLeafIcon />;
    if (tag.type_tag_detail.code == "TAG_MAPPING_AND_CERTIFICATIONS")
      return <SellIcon />;
    if (tag.type_tag_detail.code == "TAG_BUSINESS_MODEL") return <SchemaIcon />;
  }, [tag]);

  if (isError) return <>Error</>;
  if (!isSuccess) return <></>;

  return (
    <Tooltip title={tag.key_words}>
      <Chip
        label={tag.value}
        size="small"
        style={{
          borderRadius: 2,
          width: "auto",
          maxWidth: 150,
          backgroundColor: tag.type_tag_detail.color,
        }}
        icon={icon}
        {...props}
        ref={ref}
      />
    </Tooltip>
  );
});

export const DefaultTag = forwardRef((props, ref) => (
  <Chip size="small" {...props} ref={ref} />
));

export const DealFlowStateTag = forwardRef(
  ({ dealFlowCompanyState, ...props }, ref) => {
    const theme = useTheme();

    return (
      <Chip
        label={dealFlowCompanyState.label}
        size="small"
        style={{
          borderRadius: 2,
          width: "auto",
          maxWidth: 150,
          backgroundColor: theme.tag[dealFlowCompanyState.state],
        }}
        {...props}
        ref={ref}
      />
    );
  },
);

export const DealFlowTag = forwardRef(({ dealFlow, ...props }, ref) => {
  const theme = useTheme();

  return (
    <Chip
      label={dealFlow.short_name}
      size="small"
      style={{
        borderRadius: 2,
        width: "fit-content",
        backgroundColor:
          theme.palette_custom.fund[dealFlow.short_name]?.primary,
        color: theme.palette_custom.fund[dealFlow.short_name]?.secondary,
      }}
      {...props}
      ref={ref}
    />
  );
});

export const PersonTag = forwardRef(({ person, ...props }, ref) => {
  const label = person?.best_name
    ? person.best_name + ` (${person.best_email_address})`
    : person.best_email_address;

  return (
    <Tooltip title={label}>
      <Chip
        label={label}
        icon={<PersonIcon sx={{ scale: "0.8" }} />}
        size="small"
        {...props}
        ref={ref}
        style={{ borderRadius: 2, width: "auto", maxWidth: 150 }}
      />
    </Tooltip>
  );
});

export const CompanyTag = forwardRef(({ company, ...props }, ref) => {
  return (
    <Tooltip title={`${company.best_name} (${company.best_domain})`}>
      <Chip
        label={`${company.best_name} (${company.best_domain})`}
        icon={<ApartmentSharpIcon sx={{ scale: "0.8" }} />}
        size="small"
        {...props}
        ref={ref}
        style={{ borderRadius: 2, width: "auto", maxWidth: 150 }}
      />
    </Tooltip>
  );
});

export const SharingTag = forwardRef(({ sharing, onDelete, ...props }, ref) => {
  return (
    <Tooltip title={`${sharing.name}`}>
      <Chip
        label={`${sharing.name}`}
        onDelete={onDelete}
        size="small"
        {...props}
        ref={ref}
        style={{ borderRadius: 2, width: "auto", maxWidth: 150 }}
      />
    </Tooltip>
  );
});

export const SourcingStatusTag = forwardRef(({ status, sx, ...props }, ref) => {
  const { sourcingStatuses } = useContext(ConstantContext);
  const fullStatus = useMemo(() => {
    return sourcingStatuses.filter((s) => {
      return status == s.code;
    })[0];
  }, [status]);

  return (
    <Chip
      ref={ref}
      label={`${fullStatus.label}`}
      color={fullStatus.color}
      size="small"
      sx={{ ...sx, borderRadius: 2, width: "auto", maxWidth: 150 }}
      {...props}
    />
  );
});

export const InvestmentStatusTag = forwardRef(
  ({ status, sx, ...props }, ref) => {
    const { dealStatuses } = useContext(ConstantContext);
    const fullStatus = useMemo(() => {
      return dealStatuses.filter((s) => {
        return status == s.code;
      })[0];
    }, [status]);

    return (
      <Chip
        ref={ref}
        label={`${fullStatus.long}`}
        color={fullStatus.color}
        size="small"
        sx={{ ...sx, borderRadius: 2, width: "auto", maxWidth: 150 }}
        {...props}
      />
    );
  },
);

export const TeamSourceTag = forwardRef(({ teamSource, sx, ...props }, ref) => {
  return (
    <Chip
      ref={ref}
      label={teamSource.label}
      size="small"
      sx={{ borderRadius: 2, width: "auto", ...sx }}
      {...props}
    />
  );
});

export const AsyncProcessesStatusTag = forwardRef(
  ({ status, sx, ...props }, ref) => {
    const { asyncProcessesStatus } = useContext(ConstantContext);
    const fullStatus = useMemo(() => {
      return asyncProcessesStatus.filter((s) => {
        return status == s.code;
      })[0];
    }, [status]);

    return (
      <Chip
        ref={ref}
        label={`${fullStatus.label}`}
        color={fullStatus.color}
        size="small"
        sx={{ ...sx, borderRadius: 2, width: "auto", maxWidth: 150 }}
        {...props}
      />
    );
  },
);
